import { useEffect } from "react";
import { IonItem } from "@ionic/react";
import { ChainedCommands, Editor } from "@tiptap/core";
import { Placeholder } from "@tiptap/extensions";
import { Markdown as MarkdownExtension } from "@tiptap/markdown";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { IconType } from "react-icons";
import { FaBold, FaHeading, FaItalic, FaLink, FaListOl, FaListUl } from "react-icons/fa";
import Markdown from "react-markdown";

import { translate } from "~utils";

import styles from "./EditableMarkdown.module.css";
import { EditableFieldsBase } from "./types";

type EditableMarkdownProps = EditableFieldsBase & {
  isInset?: boolean;
  autoFocus?: boolean;
  onChange: (value: string) => void;
};

export const EditableMarkdown: React.FC<EditableMarkdownProps> = ({
  isEditing,
  value,
  label,
  placeholder,
  onChange,
  className,
  lines,
  isInset,
  autoFocus = true,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false } }),
      MarkdownExtension,
      Placeholder.configure({ placeholder: placeholder ?? "" }),
    ],
    content: value ?? "",
    contentType: "markdown",
    autofocus: autoFocus,
    editable: isEditing,
    editorProps: { attributes: { class: styles.markdownViewer } },
    onUpdate: ({ editor }) => onChange(editor.getMarkdown()),
  });

  // Pull in value changes that came from outside the editor (form resets, refetches)
  useEffect(() => {
    if (!editor || (value ?? "") === editor.getMarkdown()) return;

    editor.commands.setContent(value ?? "", { contentType: "markdown", emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(isEditing);
  }, [editor, isEditing]);

  return (
    <IonItem className={`${className} w-full`} lines={lines}>
      {isEditing ? (
        <div className={`w-full ${styles.editor}`}>
          {label && <p className={`font-medium ${isInset && "mt-2 mb-1"}`}>{label}</p>}
          {editor && <Toolbar editor={editor} />}
          <EditorContent editor={editor} />
        </div>
      ) : (
        <div className="flex w-full gap-4 pb-4">
          <div className={isInset ? "px-4" : ""}>
            <p className={`font-medium ${isInset && "mt-2 mb-1"}`}>{label}</p>
            {value ? (
              // Open Link in new tab
              <Markdown
                className={styles.markdownViewer}
                components={{
                  a: ({ href, children }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
                }}
              >
                {value}
              </Markdown>
            ) : (
              <span className="italic">{translate("NOT_PROVIDED")}</span>
            )}
          </div>
        </div>
      )}
    </IonItem>
  );
};

/***** Helper Components *****/
type ToolbarItem = [string, IconType, string, (chain: ChainedCommands) => ChainedCommands];

function Toolbar({ editor }: { editor: Editor }) {
  const items: ToolbarItem[] = [
    ["bold", FaBold, translate("MARKDOWN_BOLD"), (c) => c.toggleBold()],
    ["italic", FaItalic, translate("MARKDOWN_ITALIC"), (c) => c.toggleItalic()],
    ["heading", FaHeading, translate("MARKDOWN_HEADING"), (c) => c.toggleHeading({ level: 1 })],
    ["bulletList", FaListUl, translate("MARKDOWN_BULLET_LIST"), (c) => c.toggleBulletList()],
    ["orderedList", FaListOl, translate("MARKDOWN_NUMBERED_LIST"), (c) => c.toggleOrderedList()],
    ["link", FaLink, translate("MARKDOWN_LINK"), toggleLink],
  ];

  // Links need a URL, so this one asks for it instead of being a plain toggle
  function toggleLink(chain: ChainedCommands) {
    // `extendMarkRange` covers the whole link when the cursor sits inside one without a selection
    if (editor.isActive("link")) return chain.extendMarkRange("link").unsetLink();

    const href = window.prompt(translate("MARKDOWN_LINK_PROMPT"))?.trim();
    return href ? chain.extendMarkRange("link").setLink({ href }) : chain;
  }

  const active = useEditorState({
    editor,
    selector: ({ editor }) => items.map(([name]) => editor.isActive(name)),
  });

  return (
    <div className={styles.toolbar}>
      {items.map(([, Icon, label, toggle], i) => (
        <button
          key={label}
          type="button"
          className={styles.toolbarButton}
          title={label}
          aria-label={label}
          aria-pressed={active[i]}
          // Without this the button steals focus and the command loses the selection
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => toggle(editor.chain().focus()).run()}
        >
          <Icon />
        </button>
      ))}
    </div>
  );
}
